import { useReducer, useState, useCallback, useMemo, useLayoutEffect } from 'react';
import { isArray, isFunction, isNumber, memoizeFunction } from '@agilepoint/utils-common';
import { sortBy, unique } from '@agilepoint/utils-array';

export interface IHierarchicalItem {
    id: React.Key;
    label?: string;
    parentId?: React.Key;
    order?: number;
}

interface IFilteredItems<TItem extends IHierarchicalItem = IHierarchicalItem> {
    itemsWithOrderProperty: TItem[],
    itemsWithoutOrderProperty: TItem[]
}

function expandedItemsReducer(state: React.Key[], ids: React.Key | React.Key[]) {
    if (isArray(ids)) {
        return ids;
    }
    const index = state.indexOf(ids);
    if (index > -1) {
        return [...state.slice(0, index), ...state.slice(index + 1)];
    }
    return [...state, ids];
}

function useExpandedItemsState(initialExpandedItems: React.Key[] = []) {
    const [expandedItems, dispatch] = useReducer(expandedItemsReducer, initialExpandedItems);
    const toggleExpandedState = useCallback((id: React.Key) => dispatch(id), []);
    const setExpandedItems = useCallback((ids: React.Key[]) => dispatch(ids), []);
    return { expandedItems, setExpandedItems, toggleExpandedState };
}

function useSelectedItemState(defaultSelectedId?: React.Key, selectedIdFromProps?: React.Key) {
    const [selectedIdFormState, updateSelectedId] = useState(selectedIdFromProps || defaultSelectedId);
    const selectedId = selectedIdFromProps || selectedIdFormState;
    const setSelectedId = useCallback((id: React.Key) => {
        if (selectedIdFromProps !== undefined) {
            return;
        }
        updateSelectedId(id);
    }, [selectedIdFromProps]);
    return { selectedId, setSelectedId };
}

const getItemById = memoizeFunction((items: IHierarchicalItem[], id?: React.Key) => items.find(i => i.id === id));

const getItemPath = memoizeFunction((items: IHierarchicalItem[], selectedId?: React.Key) => {
    if (selectedId === undefined) {
        return [];
    }
    const itemPath = [selectedId];
    let item = getItemById(items, selectedId);
    while (item?.parentId) {
        itemPath.unshift(item.parentId);
        item = getItemById(items, item.parentId);
    }
    return itemPath;
});

const selectOrder = (item: IHierarchicalItem) => item.order;
const selectId = (item: IHierarchicalItem) => item.id;

function filterItemByOrderProperty<TItem extends IHierarchicalItem>(result: IFilteredItems<TItem>, item: TItem) {
    const { itemsWithOrderProperty, itemsWithoutOrderProperty } = result;

    isNumber(item.order) ? itemsWithOrderProperty.push(item) : itemsWithoutOrderProperty.push(item);

    return result;
}

function sortItems<TItem extends IHierarchicalItem>(items: TItem[]): TItem[] {
    const result = items.reduce(filterItemByOrderProperty, {
        itemsWithOrderProperty: [] as TItem[],
        itemsWithoutOrderProperty: [] as TItem[]
    });
    result.itemsWithOrderProperty = sortBy(result.itemsWithOrderProperty, selectOrder);
    return [...result.itemsWithOrderProperty, ...result.itemsWithoutOrderProperty];
}

function getMatchedItems<TItem extends IHierarchicalItem>(items: TItem[], searchText: string, searchPredicate?: IUseHierarchicalDataArgs<TItem>['searchPredicate']) {
    const lowerCaseSearchText = searchText.toLowerCase();
    return items.filter(i => (isFunction(searchPredicate)
        ? searchPredicate(lowerCaseSearchText, i)
        : i.label?.toLowerCase().includes(lowerCaseSearchText)));
}

const getSearchResult = memoizeFunction(<TItem extends IHierarchicalItem>(items: TItem[], searchText: string, retainHierarchyInSearchResult?: boolean, searchPredicate?: IUseHierarchicalDataArgs<TItem>['searchPredicate']) => {
    const searchResult = getMatchedItems(items, searchText, searchPredicate);

    if (!retainHierarchyInSearchResult) {
        return searchResult;
    }
    const searchResultIdsWithParents = unique(searchResult.reduce((result: React.Key[], i) => result.concat(getItemPath(items, i.id)), []));

    return searchResultIdsWithParents.map((id) => getItemById(items, id)) as TItem[];
});


function useHierarchicalDataHelperMethods<TItem extends IHierarchicalItem>(items: TItem[], searchText: string, searchPredicate?: IUseHierarchicalDataArgs<TItem>['searchPredicate'], retainHierarchyInSearchResult?: boolean) {
    const getChildItemsByParentId = useCallback((parentId?: React.Key): TItem[] => {
        if (!searchText) {
            return sortItems(items.filter(i => (i.parentId === parentId)));
        }
        const searchResult = getSearchResult(items, searchText, retainHierarchyInSearchResult, searchPredicate);
        if (!retainHierarchyInSearchResult) {
            return sortItems(searchResult);
        }
        return sortItems(searchResult.filter(i => (i.parentId === parentId)));
    }, [items, searchText, searchPredicate, retainHierarchyInSearchResult]);

    const hasChildren = useCallback((id: React.Key) => (Boolean(retainHierarchyInSearchResult || !searchText) && items.some(i => i.parentId === id)), [items, searchText, retainHierarchyInSearchResult]);

    const getItemPathById = useCallback((id?: React.Key) => getItemPath(items, id), [items]);

    return { getChildItemsByParentId, getItemPathById, hasChildren };
}

interface IUseHierarchicalDataArgs<TItem> {
    defaultSelectedId?: React.Key;
    isAccordionModeEnabled?: boolean;
    items: TItem[];
    searchText?: string;
    selectedId?: React.Key;
    retainHierarchyInSearchResult?: boolean;
    searchPredicate?(searchText: string, item: TItem): boolean;
}

export function useHierarchicalData<TItem extends IHierarchicalItem = IHierarchicalItem>(args: IUseHierarchicalDataArgs<TItem>) {
    const {
        items, defaultSelectedId, selectedId: selectedIdFromProps,
        searchText = '', isAccordionModeEnabled, searchPredicate, retainHierarchyInSearchResult
    } = args;
    const { selectedId, setSelectedId } = useSelectedItemState(defaultSelectedId, selectedIdFromProps);
    const { getChildItemsByParentId, getItemPathById, hasChildren } = useHierarchicalDataHelperMethods<TItem>(items, searchText, searchPredicate, retainHierarchyInSearchResult);
    const selectedItemPath = useMemo(() => getItemPathById(selectedId), [getItemPathById, selectedId]);
    const { expandedItems, setExpandedItems, toggleExpandedState: _toggleExpandedState } = useExpandedItemsState(selectedItemPath);

    useLayoutEffect(() => {
        if (!retainHierarchyInSearchResult || !searchText) {
            return;
        }

        const searchResult = getSearchResult(items, searchText, retainHierarchyInSearchResult, searchPredicate);

        if (!isAccordionModeEnabled) {
            setExpandedItems(searchResult.map(selectId));
            return;
        }

        const [firstItem] = sortItems(getMatchedItems(items, searchText, searchPredicate));

        if (!firstItem) {
            return;
        }

        setExpandedItems(getItemPathById(firstItem.id));
    }, [searchText, searchPredicate, items, retainHierarchyInSearchResult, setExpandedItems, isAccordionModeEnabled, getItemPathById]);

    const isInSelectedItemPath = useCallback((id: React.Key) => selectedItemPath.includes(id), [selectedItemPath]);

    const isExpanded = useCallback((id: React.Key) => expandedItems.includes(id), [expandedItems]);

    const shouldHighlightItem = useCallback((id: React.Key) => (selectedId === id || (!searchText && isInSelectedItemPath(id))), [isInSelectedItemPath, searchText, selectedId]);

    const _onSetSelectedId = useCallback((id: React.Key) => {
        if (searchText) {
            setExpandedItems(getItemPathById(id));
        }
        setSelectedId(id);
    }, [getItemPathById, searchText, setExpandedItems, setSelectedId]);

    const toggleExpandedState = useCallback((id: React.Key) => {
        if (!isAccordionModeEnabled || isExpanded(id)) {
            _toggleExpandedState(id);
            return;
        }
        setExpandedItems(getItemPathById(id));
    }, [_toggleExpandedState, getItemPathById, isAccordionModeEnabled, isExpanded, setExpandedItems]);

    return { getChildItemsByParentId, hasChildren, isExpanded, setSelectedId: _onSetSelectedId, shouldHighlightItem, toggleExpandedState, getItemPathById, setExpandedItems };
}
